import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/services/client'
import type {
  CreateReporteInput,
  Reporte,
  TipoEnvio,
  ReporteWhatsApp,
  ReporteWhatsAppResponse,
  RespuestaWhatsApp,
  CreateRespuestaWhatsAppInput,
  UpdateRespuestaWhatsAppInput,
  ScoreReporte,
  ReporteDisponible,
} from '@/modules/cronograma/types/reporte'

/**
 * Hook para crear un reporte de campaña (Email)
 */
export function useCreateReporte() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateReporteInput) => {
      const response = await apiClient.post<Reporte>('/multichannel/reportes/', data)
      return response.data
    },
    onSuccess: () => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['campanias'] })
      queryClient.invalidateQueries({ queryKey: ['reportes'] })
    },
  })
}

/**
 * Hook para crear un reporte de WhatsApp
 */
export function useCreateReporteWhatsApp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: ReporteWhatsApp) => {
      // Usar endpoint específico para WhatsApp
      const response = await apiClient.post<Reporte>('/multichannel/reportes-whatsapp/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campanias'] })
      queryClient.invalidateQueries({ queryKey: ['reportes'] })
      queryClient.invalidateQueries({ queryKey: ['reportes-whatsapp'] })
    },
  })
}

interface TiposEnvioResponse {
  items?: TipoEnvio[]
}

/**
 * Hook para obtener los tipos de envío disponibles
 */
export function useTiposEnvio() {
  return useQuery({
    queryKey: ['tipos-envio'],
    queryFn: async () => {
      const response = await apiClient.get<TipoEnvio[] | TiposEnvioResponse>('/multichannel/tipos-envio/')
      // Manejar respuesta paginada o array directo
      if (Array.isArray(response.data)) {
        return response.data
      }
      return (response.data as TiposEnvioResponse).items || []
    },
    staleTime: 0,
  })
}

/**
 * Hook para obtener reportes disponibles (para select de reporte relacionado)
 */
export function useReportesDisponibles(excludeId?: number) {
  return useQuery({
    queryKey: ['reportes-disponibles', excludeId],
    queryFn: async () => {
      const params = excludeId ? `?exclude=${excludeId}` : ''
      const response = await apiClient.get<ReporteDisponible[]>(`/multichannel/reportes/disponibles/${params}`)
      return response.data
    },
    staleTime: 0,
  })
}

/**
 * Hook para obtener el reporte Email de una campaña específica
 */
export function useReportePorCampania(campaniaId: number | null, enabled: boolean = true) {
  return useQuery({
    queryKey: ['reporte-campania', campaniaId],
    queryFn: async () => {
      if (!campaniaId) return null
      try {
        const response = await apiClient.get<Reporte>(`/multichannel/reportes/por-campania/${campaniaId}/`)
        return response.data
      } catch (error: any) {
        // Si es 404, no existe reporte - retornar null en lugar de error
        if (error?.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: enabled && !!campaniaId,
    retry: false,
    staleTime: 0, // Siempre obtener datos frescos
  })
}

/**
 * Hook para obtener el reporte WhatsApp de una campaña específica
 */
export function useReporteWhatsAppPorCampania(campaniaId: number | null, enabled: boolean = true) {
  return useQuery({
    queryKey: ['reporte-whatsapp-campania', campaniaId],
    queryFn: async () => {
      if (!campaniaId) return null
      try {
        const response = await apiClient.get<ReporteWhatsAppResponse>(`/multichannel/reportes-whatsapp/por-campania/${campaniaId}/`)
        return response.data
      } catch (error: any) {
        // Si es 404, no existe reporte - retornar null en lugar de error
        if (error?.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: enabled && !!campaniaId,
    retry: false,
    staleTime: 0, // Siempre obtener datos frescos
  })
}

// ============================================================================
// HOOKS PARA RESPUESTAS WHATSAPP
// ============================================================================

/**
 * Hook para obtener respuestas de un reporte WhatsApp
 */
export function useRespuestasWhatsApp(reporteId: number | null, enabled: boolean = true) {
  return useQuery({
    queryKey: ['respuestas-whatsapp', reporteId],
    queryFn: async () => {
      if (!reporteId) return []
      const response = await apiClient.get<RespuestaWhatsApp[]>(`/multichannel/respuestas-whatsapp/por-reporte/${reporteId}/`)
      return response.data
    },
    enabled: enabled && !!reporteId,
    staleTime: 0,
  })
}

/**
 * Hook para crear una respuesta WhatsApp
 */
export function useCreateRespuestaWhatsApp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateRespuestaWhatsAppInput) => {
      const response = await apiClient.post<RespuestaWhatsApp>('/multichannel/respuestas-whatsapp/', data)
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['respuestas-whatsapp', variables.reporte_id] })
      queryClient.invalidateQueries({ queryKey: ['reportes-whatsapp'] })
    },
  })
}

/**
 * Hook para actualizar una respuesta WhatsApp
 */
export function useUpdateRespuestaWhatsApp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateRespuestaWhatsAppInput }) => {
      const response = await apiClient.put<RespuestaWhatsApp>(`/multichannel/respuestas-whatsapp/${id}/`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['respuestas-whatsapp'] })
      queryClient.invalidateQueries({ queryKey: ['reportes-whatsapp'] })
    },
  })
}

/**
 * Hook para eliminar una respuesta WhatsApp
 */
export function useDeleteRespuestaWhatsApp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/multichannel/respuestas-whatsapp/${id}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['respuestas-whatsapp'] })
      queryClient.invalidateQueries({ queryKey: ['reportes-whatsapp'] })
    },
  })
}

// ============================================================================
// HOOKS PARA SCORE Y DISPONIBLES WHATSAPP
// ============================================================================

/**
 * Hook para obtener el score de un reporte WhatsApp
 */
export function useScoreWhatsApp(reporteId: number | null, enabled: boolean = true) {
  return useQuery({
    queryKey: ['score-whatsapp', reporteId],
    queryFn: async () => {
      if (!reporteId) return null
      try {
        const response = await apiClient.get<ScoreReporte>(`/multichannel/reportes-whatsapp/${reporteId}/score/`)
        return response.data
      } catch {
        return null
      }
    },
    enabled: enabled && !!reporteId,
    retry: false,
    staleTime: 0,
  })
}

/**
 * Hook para obtener reportes WhatsApp disponibles (para selector de redirección)
 */
/**
 * Hook para crear un cronograma de reporte WhatsApp
 */
export function useCreateCronogramaWhatsApp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ reporteId, data }: { reporteId: number; data: { fecha_hora: string; orden: number; descripcion?: string } }) => {
      const response = await apiClient.post(`/multichannel/reportes-whatsapp/${reporteId}/cronogramas/`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportes-whatsapp'] })
    },
  })
}

export function useReportesDisponiblesWhatsApp(excludeId?: number) {
  return useQuery({
    queryKey: ['reportes-whatsapp-disponibles', excludeId],
    queryFn: async () => {
      const params = excludeId ? `?exclude=${excludeId}` : ''
      const response = await apiClient.get<ReporteDisponible[]>(`/multichannel/reportes-whatsapp/disponibles/${params}`)
      return response.data
    },
    staleTime: 0,
  })
}
